import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SectionHeader from 'components/containers/sectionHeader';
import { stopPropagationAndPreventDefault } from 'components/searchAndFilter/utils';
import { RMCustomButtonProps } from 'components/customComponents/CommonComponents/RMCustomButton/RMCustomButton';
import { IStyle } from 'Styles';
import { IDropDownList } from 'components/customComponents/CommonComponents/RMDropDownList';
import ValuesSelectionModalBody from 'components/sidePanel/searchAndFilter/filter/ValuesSelectionModalBody';
import { useModalHandlers } from 'components/modal';
import { FilterDataDTO, MIN_FILTER_VALUES, ValueType } from '../constants';
import {
  useSearchFilterOriginalValues,
  useSearchFilters,
  useSearchResultFilterValues,
  useSearchTargetFields,
} from 'redux/hooks/searchnFilter';
import Chip from 'components/Chip';
import ChipContainer from 'components/ChipContainer';
import { useRMDispatch } from 'redux/hooks';
import {
  invertSearchFilterValues,
  resetSearchFilterValues,
  addSearchFilterValues,
  toggleSelectAllSearchFilterValues,
  addSearchFilterValue,
  removeSearchFilterValue,
  resetFilterSelectedValues,
} from 'redux/reducers/searchnFilterReducer';
import SliderWithButtons from './SliderWithButtons';
import { getFilterValueDTO, getMinMaxDates } from '../utils';
import FilterDateSelection from './FilterDateSelection';
import RMSvgIcon from 'components/customComponents/CommonComponents/RMSvgIcon';
import { FmlxModal } from 'fmlx-common-ui';
import isEqual from 'lodash/isEqual';

export interface Props {
  filter: FilterDataDTO | undefined;
  primaryFilterId?: number;
  setPrimaryFilterId?: (id: number) => void;
  disabled?: boolean;
}

const FilterCollapsibleSection = (props: Props) => {
  const { filter, primaryFilterId, setPrimaryFilterId, disabled } = props;
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
  const { showModal, hideModal } = useModalHandlers();
  const [listView, setListView] = useState<boolean>(true);
  const originalValues = useSearchFilterOriginalValues(filter.id);
  const searchTargetFields = useSearchTargetFields();
  const filters = useSearchFilters();
  const dispatch = useRMDispatch();
  const fetchFilterAllValues = useSearchResultFilterValues(filter.searchFieldId);

  const filterTitle = useMemo(() => {
    const filterTitle = searchTargetFields?.find((ff) => {
      return ff.searchFields.some((s) => s.id === filter.searchFieldId);
    });
    return filterTitle
      ? filterTitle.searchFields.find((s) => s.id === filter.searchFieldId)
          .fieldAndTableDisplayName
      : '';
  }, [searchTargetFields, filter]);

  const filterType = useMemo(() => {
    const filterType = searchTargetFields?.find((ff) => {
      return ff.searchFields.some((s) => s.id === filter.searchFieldId);
    });
    return filterType
      ? filterType.searchFields.find((s) => s.id === filter.searchFieldId).fieldClass
      : '';
  }, [searchTargetFields, filter]);

  const isValueSelected = useCallback(
    (value: string | null) => {
      return filter.values.some((f) => f.value === value);
    },
    [filters],
  );

  const toggleChipValue = (text: string) => {
    const newValue = getFilterValueDTO(filter.id, text);
    const valueIndex = filter.values.findIndex((v) => v.value === text);
    if (valueIndex !== -1) {
      dispatch(removeSearchFilterValue(newValue));
    } else {
      dispatch(addSearchFilterValue(newValue));
    }
  };

  const handleChipDoubleClick = (text: string) => {
    dispatch(addSearchFilterValues({ filterId: filter.id, values: [text] }));
  };

  const handleCollapse = (e: any) => {
    stopPropagationAndPreventDefault(e);
    setIsCollapsed((prvState) => !prvState);
  };

  const onListViewClick = () => {
    setListView(true);
  };

  const onSliderViewClick = (e) => {
    stopPropagationAndPreventDefault(e);
    setListView(false);
  };

  const panelFilterButtons: RMCustomButtonProps[] = [
    {
      iconProps: { name: 'ChecklistFilter', width: '20px', height: '20px' },
      onClick: onListViewClick,
      buttonMainContainerStyle: listView
        ? styles.checkListFilterEnabled
        : styles.checkListFilter,
      mainContainerClass: disabled ? 'opacity-50 pointer-events-none' : '',
      titleTip: 'List View',
    },
    {
      iconProps: { name: 'RadioFilter', width: '20px', height: '20px' },
      onClick: onSliderViewClick,
      buttonMainContainerStyle: !listView
        ? styles.radioFilterEnabled
        : styles.radioFilter,
      mainContainerClass:
        disabled ||
        filter.selectedValues === undefined ||
        filter.selectedValues?.length <= 1
          ? 'opacity-50 pointer-events-none'
          : '',
      titleTip: 'Range View',
    },
  ];

  const onSelectAll = () => {
    dispatch(
      toggleSelectAllSearchFilterValues({
        filterId: filter.id,
        selectAll: true,
        allValues: false,
      }),
    );
  };

  const onUnselectAll = () => {
    dispatch(
      toggleSelectAllSearchFilterValues({
        filterId: filter.id,
        selectAll: false,
        allValues: false,
      }),
    );
  };

  const onInvertSelection = () => {
    dispatch(invertSearchFilterValues({ filterId: filter.id }));
  };

  const selectRange = (idx: number[]) => {
    const arr = filter.selectedValues.slice(idx[0], idx[1] + 1);
    dispatch(addSearchFilterValues({ filterId: filter.id, values: arr }));
  };

  const onResetOption = () => {
    if (originalValues) {
      dispatch(resetSearchFilterValues({ filterId: filter.id, values: originalValues }));
      dispatch(
        resetFilterSelectedValues({
          filterId: filter.id,
          values: originalValues.map((v) => v.value),
        }),
      );
    }
  };

  const shouldEnableReset = () => {
    return !originalValues || isEqual(originalValues, filter.values);
  };

  const ExtraOptions: IDropDownList[] = [
    {
      label: 'Select All',
      onClick: onSelectAll,
      isDisabled: false,
      itemClass: 'h-[3.5vh]',
    },
    {
      label: 'Unselect All',
      onClick: onUnselectAll,
      isDisabled: false,
      itemClass: 'h-[3.5vh]',
    },
    {
      label: 'Invert Selection',
      onClick: onInvertSelection,
      isDisabled: false,
      itemClass: 'h-[3.5vh]',
    },
    {
      label: 'Reset',
      onClick: onResetOption,
      isDisabled: shouldEnableReset(),
      itemClass: 'h-[3.5vh]',
    },
  ];

  const panelFilterButtonsSizeDetails = { width: '20px', height: '20px' };

  const onClickSelectValues = () => {
    if (filter.isFetching) {
      return;
    }
    showModal({
      body: (
        <ValuesSelectionModalBody
          title={filterTitle}
          filterId={filter.id}
          valueType={filterType}
          hideModal={hideModal}
        />
      ),
      heading: 'Add Filter Items',
      showButtons: false,
      scrollable: false,
      size: FmlxModal.Size.EXTRA_LARGE,
    });
  };

  const prioritizePanel = () => {
    if (primaryFilterId === filter.id) {
      setPrimaryFilterId(undefined);
    } else {
      setPrimaryFilterId(filter.id);
    }
  };

  const onClickMasterFilter = () => {
    prioritizePanel();
  };

  function getRibbonClassName() {
    let className = '';
    if (primaryFilterId === filter.id) {
      className += ' bg-black';
    }
    if (disabled) {
      className += ' opacity-50 pointer-events-none';
    }
    return className;
  }

  const panelSideButtons: RMCustomButtonProps[] = [
    {
      iconProps: {
        ...panelFilterButtonsSizeDetails,
        name: primaryFilterId === filter.id ? 'BookmarkWhite' : 'BookmarkBlack',
        fill: primaryFilterId === filter.id ? 'black' : undefined,
        title: 'Master Filter',
      },
      onClick: onClickMasterFilter,
      mainContainerClass: getRibbonClassName(),
    },
    {
      iconProps: { ...panelFilterButtonsSizeDetails, name: 'Tag' }, //Select values
      onClick: onClickSelectValues,
      mainContainerClass: disabled ? 'opacity-50 pointer-events-none' : '',
      titleTip: 'Filter Items',
    },
    {
      iconProps: { ...panelFilterButtonsSizeDetails, name: 'ThreeDotMenu' },
      dropDownList: ExtraOptions,
      onClick: stopPropagationAndPreventDefault,
      mainContainerClass: disabled ? 'opacity-50 pointer-events-none' : '',
      titleTip: 'Menu',
    },
    {
      iconProps: { ...panelFilterButtonsSizeDetails, name: 'ChevronDownCircle' },
      onClick: handleCollapse,
      contentContainerStyle: { rotate: `${!isCollapsed ? '' : '-90deg'} ` },
      titleTip: `${!isCollapsed ? 'Collapse' : 'Expand'}`,
    },
  ];

  const onDateChange = (values: string[]) => {
    dispatch(addSearchFilterValues({ filterId: filter.id, values }));
  };

  const filterBody = useMemo(() => {
    if (!filter.selectedValues?.length && filter.allValues?.length > MIN_FILTER_VALUES) {
      return (
        <div
          id="filtercollapsiblesection-filter-body-wrapper"
          className="flex flex-col items-center justify-center my-2"
        >
          <RMSvgIcon
            id="filtercollapsiblesection-filter-addtag-icon"
            name={'AddTag'}
            subfolder="FilterPanel"
            width={36}
            height={36}
          />
          <p
            id="filtercollapsiblesection-filter-body-message"
            className="text-gray-500 m-0"
          >
            {"Please select 'ADD FILTER' to add filter parameters"}
          </p>
        </div>
      );
    }
    if (listView || filterType === ValueType.STRING) {
      const chipsList = filter.selectedValues?.map((fv) => {
        if (filterType === ValueType.DATE || filterType === ValueType.DATE_IMAGED) {
          return (
            <Chip
              onClick={toggleChipValue}
              onDoubleClick={handleChipDoubleClick}
              key={fv}
              text={fv}
              isSelected={isValueSelected(fv)}
            />
          );
        }
        return (
          <Chip
            onClick={toggleChipValue}
            onDoubleClick={handleChipDoubleClick}
            key={fv ? fv.toString() : 'None'}
            text={fv}
            isSelected={isValueSelected(fv)}
          />
        );
      });
      return <ChipContainer chipsList={chipsList} />;
    } else if (filterType === ValueType.INTEGER || filterType === ValueType.NUMBER) {
      const values = filter.selectedValues?.map((v) => (v ? parseFloat(v) : null));
      const sortedValues = values
        ? values.filter((v) => v !== null).sort((a, b) => a - b)
        : [];
      return (
        <SliderWithButtons
          values={sortedValues}
          minValue={
            filter?.values?.length
              ? Math.min(...filter.values?.map((x) => parseFloat(x.value)))
              : 0
          }
          maxValue={
            filter?.values?.length
              ? Math.max(...filter.values?.map((x) => parseFloat(x.value)))
              : 0
          }
          onFinalChange={selectRange}
        />
      );
    } else if (filterType === ValueType.DATE || filterType === ValueType.DATE_IMAGED) {
      const values = filter.values.map((f) => f.value);
      const [minDate, maxDate] = getMinMaxDates(values ?? filter.selectedValues);
      return (
        <FilterDateSelection
          selectedValues={filter.selectedValues}
          selectedFilters={filter.values}
          minDate={minDate}
          maxDate={maxDate}
          onChange={onDateChange}
        />
      );
    }
  }, [filter, listView, filterType]);

  useEffect(() => {
    if (filter && !filter.allValues && !filter.isFetching) {
      fetchFilterAllValues();
    }
  }, [filter]);

  return (
    <div id="filtercollapsiblesection-main-container">
      <SectionHeader
        title={filterTitle}
        onClick={handleCollapse}
        panelSideButtons={panelSideButtons}
        {...{
          panelLeftButtons: filterType !== ValueType.STRING ? panelFilterButtons : [],
        }}
      />
      {filter.isFetching && (
        <div id="filtercollapsiblesection-loading" className="w-full py-2 text-center">
          Loading...
        </div>
      )}
      {!filter.isFetching && !isCollapsed && (
        <div
          id="filtercollapsiblesection-filter-body"
          className={`visible ${disabled ? 'opacity-50 pointer-events-none' : ''}`}
        >
          {filterBody}
        </div>
      )}
    </div>
  );
};

export default FilterCollapsibleSection;

const styles: IStyle = {
  checkListFilter: {
    border: '1px solid #b4b4b4',
    borderRadius: '4px 0px 0px 4px',
    padding: '4px',
    height: '24px',
  },
  checkListFilterEnabled: {
    border: '1px solid #b4b4b4',
    borderRadius: '4px 0px 0px 4px',
    padding: '4px',
    height: '24px',
    background: '#85baff',
  },
  radioFilter: {
    border: '1px solid #b4b4b4',
    borderRadius: '0px 4px 4px 0px',
    padding: '4px',
    height: '24px',
  },
  radioFilterEnabled: {
    border: '1px solid #b4b4b4',
    borderRadius: '0px 4px 4px 0px',
    padding: '4px',
    height: '24px',
    background: '#85baff',
  },
  modify: {
    background: '#4CAF50',
    width: '70px',
    height: '26px',
    padding: '4px, 10px, 4px, 10px',
    borderRadius: '4px',
    gap: '8px',
    color: 'white',
  },
  reset: {
    background: '#313131',
    width: '70px',
    height: '26px',
    padding: '4px, 10px, 4px, 10px',
    borderRadius: '4px',
    color: 'white',
  },
  cancel: {
    color: '#4caf50 ',
    background: '#ffffff',
    borderStyle: 'solid',
    borderColor: '#4caf50',
    borderWidth: '1px',
    borderRadius: '4px',
    width: '70px',
    height: '26px',
    padding: '4px, 10px, 4px, 10px',
  },
  applyDisabled: {
    background: '#CACACA',
    width: '70px',
    height: '26px',
    padding: '4px, 10px, 4px, 10px',
    borderRadius: '4px',
  },
  applyEnabled: {
    background: '#4CAF50',
    width: '70px',
    height: '26px',
    padding: '4px, 10px, 4px, 10px',
    borderRadius: '4px',
    color: 'white',
  },
};
